import { keyframes } from "@emotion/react";
import { Box, SxProps } from "@outschool/backpack";
import React from "react";
const generateScrollKeyframes = (
  numTexts: number,
  pauseDuration: number,
  transitionDuration: number
) => {
  const timePerImage = pauseDuration + transitionDuration;
  const totalDuration = numTexts * timePerImage;

  let keyframes = "";
  for (let i = 0; i < numTexts; i++) {
    const pauseStartTime = i * timePerImage;
    const pauseEndTime = pauseStartTime + pauseDuration;

    const pauseStartPercent = (pauseStartTime / totalDuration) * 100;
    const pauseEndPercent = (pauseEndTime / totalDuration) * 100;
    const translateYPercent = (50 / numTexts) * i;
    keyframes += `  ${pauseStartPercent}%, ${pauseEndPercent}% { transform: translateY(-${translateYPercent}%); }`;
  }

  keyframes += "  100% { transform: translateY(-50%); }";

  return keyframes;
};

//An approximation of the Figma ease-in-out curve:
//https://help.figma.com/hc/en-us/articles/360051748654-Prototype-easing-and-spring-animations#h_01EF10YAE8MPP9YBSPS41WKZC8
const EaseInAndOutFigma = "cubic-bezier(.56,-0.4,.36,1.2)";
const RollingText = ({
  texts,
  textSx = {},
  animationTimingFunction = EaseInAndOutFigma,
  pauseSeconds = 3,
  transitionSeconds = 0.6,
}: {
  texts: string[];
  textSx?: SxProps;
  animationTimingFunction?: string;
  pauseSeconds: number;
  transitionSeconds: number;
}) => {
  const longestTextLength = Math.max(...texts.map(text => text.length));
  const longestText = texts.find(text => text.length === longestTextLength);
  const totalDuration = texts.length * (pauseSeconds + transitionSeconds);

  const scroll = keyframes`
  ${generateScrollKeyframes(texts?.length, pauseSeconds, transitionSeconds)}
`;

  //To correctly size the "peephole" window, we put in the longest text in the list
  //But with a transparent color so it doesn't show up. This makes the browser
  //size the peephole window as if the longest text was there.
  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        color: "transparent",
      }}
    >
      {longestText}
      <Box
        sx={{
          ...(textSx ?? {}),
          position: "absolute",
          width: "max-content",
          top: 0,
          display: "inline-flex",
          alignItems: "start",
          flexDirection: "column",
          animation: `${scroll} ${totalDuration}s ${animationTimingFunction} infinite`,
        }}
      >
        {[...texts, ...texts].map((text, index) => (
          <Box key={`rolling-${index}`}>{text}</Box>
        ))}
      </Box>
    </Box>
  );
};

export default RollingText;
